/**
 * Portal Manager - Production Implementation
 *
 * Handles portal functionality including display, caching, and cookie management.
 * Works with both logged-in users (customer metafield) and anonymous (cookie) users
 *
 * Features:
 * - localStorage caching with 8-hour expiration
 * - Cookie-based tracking for anonymous users
 * - Credit user tracking (credit_user URL param, tied to portal session)
 * - Storefront API integration with caching
 * - Shop Consultant Bar integration (full-width bar moveable via Shopify sections)
 * - Cart attribute tracking (portal_session, credit_user)
 * - Debug mode (add ?debug to URL; requires Theme Settings > Portal Manager > Enable debug mode)
 *
 * URL PARAMETERS:
 * - portal_id: Sets the portal context (e.g., ?portal_id=abc123)
 * - credit_user: Tracks credit attribution (e.g., ?credit_user=C1234567)
 *   Format: C (customer) or R (UnFranchise Owner repID) followed by digits, max 16 chars
 *   Cleared automatically when portal_id changes
 *
 * CONFIGURATION:
 * The storefrontToken is configured in Theme Settings > Portal Manager.
 * Values are passed via window.portalManagerConfig from snippets/portal-manager.liquid.
 *
 * INTEGRATION:
 * The shop-consultant-bar section handles display for both logged-in and
 * anonymous users. This script handles cookie-based anonymous users by
 * showing the bar and populating the consultant name via JavaScript.
 *
 * @module PortalManager
 */

/**
 * @typedef {Object} PortalManagerConfig
 * @property {string} [storefrontToken]
 * @property {number} [cacheExpirationHours]
 * @property {number} [cookieExpirationDays]
 * @property {Object<string, string>} [cartAttributes] - Cart attributes from Liquid at page render time, used to skip redundant /cart/update.js calls
 * @property {boolean} [debugEnabled] - Theme Settings > Portal Manager > Enable debug mode. Master switch for ?debug; also gates whether the debug panel markup is rendered at all
 */

/**
 * @typedef {Object} PortalData
 * @property {string} presented_by
 * @property {string} [email_address] - Consultant recipient for the Contact page's direct-email form (MA-1852)
 * @property {string} [portal_id] - The portal's own id field, used as the form's portalID
 * @property {string} [show_address_and_phone] - "true"/"false"; whether the UFO shows phone + address
 * @property {string} [phone_number] - Consultant phone, shown on the Contact page when enabled
 * @property {string} [formatted_address] - Consultant address, shown on the Contact page when enabled
 * @property {string} [display_profile_picture] - "true"/"false"; whether to show the consultant avatar
 * @property {string} [profile_image] - Consultant avatar image src (used directly, as the server does)
 * @property {string} [google_analytics_id] - GA4 measurement ID (e.g. "G-ABC123XYZ"), mirrored to the portal_ga4 cookie
 * @property {string} [autoship_discount] - Subscriber discount percentage override for this portal
 * @property {string} [vift_connect_active] - "true"/"false"; whether this portal offers VIFT Connect (assets/cart-vift-connect-sync.js, cart line property _CLP2)
 */

/**
 * Global window properties set by Liquid templates
 * @typedef {Window & {
 *   portalManagerConfig?: PortalManagerConfig,
 *   customerPortalId?: string,
 *   customerDoNotSell?: boolean,
 *   shopConsultantBarConfig?: Object,
 *   PortalManager?: { getPortalId: () => string | null, getPortalData: () => Promise<PortalData | null>, getCreditUser: () => string | null }
 * }} PortalWindow
 */

(function () {
  "use strict";

  // Configuration - Values loaded from theme settings via window.portalManagerConfig
  /** @type {PortalWindow} */
  const portalWindow = window;
  const globalConfig = portalWindow.portalManagerConfig || {};
  const CONFIG = {
    storefrontToken: globalConfig.storefrontToken || "",
    cacheExpiration: (globalConfig.cacheExpirationHours || 8) * 60 * 60 * 1000, // Convert hours to milliseconds
    cookieExpiration: globalConfig.cookieExpirationDays || 30, // Days
  };

  // Cookie mirror of the apim-auth-token meta tag, for links that can't run JS.
  const AUTH_TOKEN_COOKIE = "apim-auth-token";
  const AUTH_TOKEN_DAYS = 1; // 24h, matching the token/page-cache window

  // Top-domain cookie mirroring the portal's google_analytics_id (e.g. "G-ABC123XYZ"),
  // for downstream GA4 tracking. Set/cleared via setCookie/clearCookie so it shares the
  // registrable domain (and lifetime) with portal_id.
  const GA4_COOKIE = "portal_ga4";
  const GA4_ID_PATTERN = /^G-[A-Z0-9]+$/i;

  // Bumped whenever the cached PortalData shape changes, so entries written by an
  // older version are discarded (and refetched) instead of serving missing fields.
  const PORTAL_CACHE_VERSION = 4;

  // Cached registrable cookie domain (".example.com", or "" for host-only). Declared
  // here so it's initialized before the portal-sync block below calls setCookie().
  let cookieDomainCache;

  // Check if debug mode is enabled. Requires BOTH the theme setting (Theme Settings >
  // Portal Manager > Enable debug mode) and ?debug in the URL, so the param alone can't
  // surface debug output on a production store. When the setting is off the debug panel
  // markup isn't rendered either (see snippets/portal-manager.liquid).
  const isDebugMode =
    globalConfig.debugEnabled === true && new URLSearchParams(window.location.search).has("debug");

  // Get current state from Liquid
  const hasCustomerPortal = portalWindow.customerPortalId ? true : false;
  const currentPortalId = portalWindow.customerPortalId;

  /**
   * Debug state tracking
   * @type {{
   *   apiFetchAttempted: boolean,
   *   apiFetchSuccess: boolean,
   *   apiFetchError: string | null,
   *   apiConfigMissing: boolean,
   *   portalData: string | null,
   *   portalSource: string | null,
   *   cacheHit: boolean,
   *   cacheMiss: boolean,
   *   creditUser: string | null
   * }}
   */
  const debugState = {
    apiFetchAttempted: false,
    apiFetchSuccess: false,
    apiFetchError: null,
    apiConfigMissing: false,
    portalData: null,
    portalSource: null,
    cacheHit: false,
    cacheMiss: false,
    creditUser: null,
  };

  if (isDebugMode) {
    console.log("Portal Manager initialized (DEBUG MODE)", {
      hasCustomerPortal: hasCustomerPortal,
      currentPortalId: currentPortalId,
      currentUrl: window.location.href,
    });
  }

  // Handle logged-in user context: sync cookie and cache with customer metafield
  // Customer metafield is the source of truth; cookie persists if they log out
  if (hasCustomerPortal && currentPortalId) {
    // Validate customer metafield value before syncing
    if (!isValidPortalId(currentPortalId)) {
      console.warn("Invalid portal_id format in customer metafield:", currentPortalId);
      // Clear any stale cookie/cache from previous invalid state
      clearCookie("portal_id");
      clearCookie(GA4_COOKIE);
      clearPortalCache(); // Clear all portal cache entries
    } else {
      setCookie("portal_id", currentPortalId, CONFIG.cookieExpiration);

      // Fetch and cache portal data if not already cached; fetchPortalData syncs
      // the GA4 cookie on success, so handle the cache-hit case explicitly here.
      // The Contact page's consultant block is rendered hidden for every visitor and
      // filled here too, so the portal is only ever resolved by this script.
      const cachedData = getCachedPortalData(currentPortalId);
      if (!cachedData) {
        fetchPortalData(currentPortalId, "customer metafield").then(() => {
          renderContactConsultant(getCachedPortalData(currentPortalId));
        });
      } else {
        syncGa4Cookie(cachedData);
        renderContactConsultant(cachedData);
      }

      if (isDebugMode) {
        console.log("Logged-in user: cookie and cache synced with metafield:", currentPortalId);
      }
    }
  }

  // ============================================================================
  // COOKIE MANAGEMENT
  // ============================================================================

  /**
   * Retrieve a cookie value by name
   * @param {string} name - The cookie name to look up
   * @returns {string|null} The decoded cookie value, or null if not found
   */
  function getCookie(name) {
    const cookies = document.cookie.split(";");
    for (const cookie of cookies) {
      const [cookieName, ...cookieValue] = cookie.trim().split("=");
      if (cookieName === name) {
        return decodeURIComponent(cookieValue.join("=") || "");
      }
    }
    return null;
  }

  /**
   * Broadest domain the browser will accept a cookie on, so cookies are shared
   * across subdomains (e.g. www. and account.). Derived at runtime because the
   * theme runs on 8 TLDs including multi-part ones (.com.au, .com.hk) that string
   * math can't strip. No JS API exposes this, so we probe candidates broadest-first
   * and keep the first that sticks (the browser rejects public suffixes). Returns
   * "" for hosts with no settable parent (*.myshopify.com previews, localhost, IPs).
   * @returns {string}
   */
  function getCookieDomain() {
    if (cookieDomainCache !== undefined) return cookieDomainCache;

    const host = location.hostname;
    const labels = host.split(".");
    cookieDomainCache = ""; // host-only fallback for IPs, single-label hosts, public-suffix-only hosts
    if (labels.length < 2 || /^[\d.]+$/.test(host)) return cookieDomainCache;

    const probe = "__pm_domain_probe__";
    for (let i = labels.length - 2; i >= 1; i--) {
      const candidate = "." + labels.slice(i).join(".");
      document.cookie = `${probe}=1; Domain=${candidate}; path=/; SameSite=Lax`;
      const accepted = document.cookie.indexOf(`${probe}=1`) !== -1;
      document.cookie = `${probe}=; Domain=${candidate}; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC; SameSite=Lax`;
      if (accepted) {
        cookieDomainCache = candidate;
        break;
      }
    }
    return cookieDomainCache;
  }

  /**
   * Set a cookie with the specified name, value, and expiration
   * @param {string} name - The cookie name
   * @param {string} value - The cookie value
   * @param {number} days - Number of days until expiration
   * @returns {void}
   */
  function setCookie(name, value, days) {
    const expirationDate = new Date();
    expirationDate.setDate(expirationDate.getDate() + days);
    const domain = getCookieDomain();
    const domainPart = domain ? `; Domain=${domain}` : "";
    const secure = location.protocol === "https:" ? "; Secure" : "";
    const cookieValue = `${name}=${encodeURIComponent(value)}${domainPart}; expires=${expirationDate.toUTCString()}; path=/; SameSite=Lax${secure}`;
    document.cookie = cookieValue;
  }

  /**
   * Clear a cookie by setting its expiration to the past
   * @param {string} name - The cookie name to clear
   * @returns {void}
   */
  function clearCookie(name) {
    const domain = getCookieDomain();
    // Domain must match setCookie's, otherwise the browser only clears a
    // host-only cookie and the domain-scoped cookie survives.
    const domainPart = domain ? `; Domain=${domain}` : "";
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${name}=${domainPart}; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; SameSite=Lax${secure}`;
  }

  /**
   * Mirror the apim-auth-token meta tag into a cookie so links that can't run JS
   * carry it. The meta renders only for logged-in customers and may come from
   * Shopify's page cache, so we re-sync each load and clear the cookie when absent.
   * @returns {void}
   */
  function syncApimAuthTokenCookie() {
    const meta = document.querySelector('meta[name="apim-auth-token"]');
    const token = meta ? meta.content : "";
    if (token) {
      if (getCookie(AUTH_TOKEN_COOKIE) !== token) setCookie(AUTH_TOKEN_COOKIE, token, AUTH_TOKEN_DAYS);
    } else if (getCookie(AUTH_TOKEN_COOKIE) !== null) {
      clearCookie(AUTH_TOKEN_COOKIE);
    }
  }

  /**
   * Sync the portal_ga4 cookie with the resolved portal's google_analytics_id.
   * Only a valid GA4 measurement ID (e.g. "G-ABC123XYZ") is written; any other
   * value (empty, malformed) clears the cookie so it never reflects a previous portal.
   * @param {PortalData|null} portalData
   * @returns {void}
   */
  function syncGa4Cookie(portalData) {
    const gaId = portalData && portalData.google_analytics_id ? String(portalData.google_analytics_id).trim() : "";
    if (gaId && GA4_ID_PATTERN.test(gaId)) {
      setCookie(GA4_COOKIE, gaId, CONFIG.cookieExpiration);
      if (isDebugMode) console.log("portal_ga4 cookie set:", gaId);
    } else {
      if (gaId && isDebugMode) console.warn("Invalid google_analytics_id, clearing portal_ga4:", gaId);
      clearCookie(GA4_COOKIE);
    }
  }

  // ============================================================================
  // CREDIT USER VALIDATION
  // ============================================================================

  /**
   * Validate portal_id format: alphanumeric with hyphens/underscores, max 64 chars
   * @param {string} portalId - The portal_id value to validate
   * @returns {boolean} True if valid, false otherwise
   */
  function isValidPortalId(portalId) {
    if (!portalId || typeof portalId !== "string") return false;
    // Alphanumeric with hyphens and underscores, reasonable max length
    const pattern = /^[a-zA-Z0-9_-]+$/;
    return pattern.test(portalId) && portalId.length <= 64;
  }

  /**
   * Validate credit_user format: C (customer) or R (UFO repID) followed by digits, max 16 chars
   * @param {string} creditUser - The credit_user value to validate
   * @returns {boolean} True if valid, false otherwise
   */
  function isValidCreditUser(creditUser) {
    if (!creditUser || typeof creditUser !== "string") return false;
    // Format: C (customer) or R (UnFranchise Owner repID) followed by digits, max 16 characters
    const pattern = /^[CR]\d+$/;
    return pattern.test(creditUser) && creditUser.length <= 16;
  }

  /**
   * Get the current credit_user from cookie
   * @returns {string|null} The credit_user value, or null if not set
   */
  function getCreditUser() {
    return getCookie("credit_user");
  }

  // ============================================================================
  // LOCALSTORAGE CACHE MANAGEMENT
  // ============================================================================

  /**
   * Check if localStorage is available and functional
   * @returns {boolean} True if localStorage is available, false otherwise
   */
  function isLocalStorageAvailable() {
    try {
      const test = "__localStorage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Retrieve cached portal data from localStorage
   * @param {string} portalId - The portal ID to look up
   * @returns {PortalData|null} The cached portal data object, or null if not found or expired
   */
  function getCachedPortalData(portalId) {
    if (!isLocalStorageAvailable()) return null;

    try {
      const cached = localStorage.getItem(`portal_cache_${portalId}`);
      if (!cached) return null;

      const cacheEntry = JSON.parse(cached);

      // Discard entries written by an older schema (missing newer fields like
      // google_analytics_id/autoship_discount), forcing a one-time refetch.
      if (cacheEntry.version !== PORTAL_CACHE_VERSION) {
        localStorage.removeItem(`portal_cache_${portalId}`);
        if (isDebugMode) console.log("Portal cache version mismatch for", portalId, "- discarding");
        return null;
      }

      // Check if cache is expired (8 hours)
      if (cacheEntry.expires_at <= Date.now()) {
        localStorage.removeItem(`portal_cache_${portalId}`);
        if (isDebugMode) console.log("Portal cache expired for", portalId);
        return null;
      }

      if (isDebugMode) console.log("Portal cache hit for", portalId);
      debugState.cacheHit = true;
      return cacheEntry.data;
    } catch (error) {
      console.error("Error reading portal cache:", error);
      return null;
    }
  }

  /**
   * Store portal data in localStorage cache
   * @param {string} portalId - The portal ID to cache
   * @param {PortalData} portalData - The portal data object to cache
   * @returns {void}
   */
  function setCachedPortalData(portalId, portalData) {
    if (!isLocalStorageAvailable()) return;

    try {
      const cacheEntry = {
        version: PORTAL_CACHE_VERSION,
        portal_id: portalId,
        data: portalData,
        cached_at: Date.now(),
        expires_at: Date.now() + CONFIG.cacheExpiration,
      };

      localStorage.setItem(`portal_cache_${portalId}`, JSON.stringify(cacheEntry));
      if (isDebugMode) console.log("Portal data cached for", portalId);
    } catch (error) {
      console.error("Error caching portal data:", error);
    }
  }

  /**
   * Clear portal cache from localStorage
   * @param {string} [portalId] - Optional portal ID to clear; if omitted, clears all portal cache entries
   * @returns {void}
   */
  function clearPortalCache(portalId) {
    if (!isLocalStorageAvailable()) return;

    try {
      if (portalId) {
        localStorage.removeItem(`portal_cache_${portalId}`);
        if (isDebugMode) console.log("Portal cache cleared for", portalId);
      } else {
        // Clear all portal cache entries
        const keysToRemove = [];
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          if (key && key.startsWith("portal_cache_")) {
            keysToRemove.push(key);
          }
        }
        keysToRemove.forEach(key => localStorage.removeItem(key));
        if (isDebugMode) console.log("All portal cache cleared");
      }
    } catch (error) {
      console.error("Error clearing portal cache:", error);
    }
  }

  // ============================================================================
  // STOREFRONT API INTEGRATION
  // ============================================================================

  /**
   * Fetch portal data from the Storefront API
   * @param {string} portalId - The portal ID to fetch
   * @param {string} source - Description of where this request originated (for debugging)
   * @returns {Promise<string|null>} The presented_by value, or null if not found
   */
  async function fetchPortalData(portalId, source) {
    // Validate configuration before attempting API calls
    if (!CONFIG.storefrontToken) {
      console.warn(
        "Portal Manager: Storefront API token not configured. Configure it in Theme Settings > Portal Manager."
      );
      debugState.apiConfigMissing = true;
      debugState.apiFetchError = "missing_storefront_token";
      debugState.portalSource = source || "config_error (missing storefront token)";
      if (isDebugMode) updateDebugInfo();
      return null;
    }

    // Check cache first
    const cachedData = getCachedPortalData(portalId);
    if (cachedData) {
      // Keep the top-domain GA4 cookie in sync even when serving from cache
      syncGa4Cookie(cachedData);
      debugState.portalData = cachedData.presented_by;
      debugState.portalSource = `${source} (cached)`;
      if (isDebugMode) updateDebugInfo();
      return cachedData.presented_by;
    }

    // Cache miss - fetch from API
    debugState.cacheMiss = true;
    debugState.apiFetchAttempted = true;
    debugState.portalSource = source;
    if (isDebugMode) console.log("Portal cache miss for", portalId, "- fetching from API");

    const query = `
      query getPortal($handle: String!) {
        metaobject(handle: {type: "portals", handle: $handle}) {
          presented_by: field(key: "presented_by") { value }
          email_address: field(key: "email_address") { value }
          portal_id: field(key: "portal_id") { value }
          show_address_and_phone: field(key: "show_address_and_phone") { value }
          phone_number: field(key: "phone_number") { value }
          formatted_address: field(key: "formatted_address") { value }
          display_profile_picture: field(key: "display_profile_picture") { value }
          profile_image: field(key: "profile_image") { value }
          google_analytics_id: field(key: "google_analytics_id") { value }
          autoship_discount: field(key: "autoship_discount") { value }
          vift_connect_active: field(key: "vift_connect_active") { value }
        }
      }
    `;

    try {
      const apiVersion = (window.Theme && window.Theme.storefront_api_version) || "2026-07";
      const response = await fetch("/api/" + apiVersion + "/graphql.json", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Shopify-Storefront-Access-Token": CONFIG.storefrontToken,
        },
        body: JSON.stringify({
          query: query,
          variables: { handle: portalId },
        }),
      });

      // Check HTTP response status
      if (!response.ok) {
        const errorMsg = `HTTP ${response.status}: ${response.statusText}`;
        console.error("Storefront API HTTP error:", errorMsg);
        debugState.apiFetchSuccess = false;
        debugState.apiFetchError = errorMsg;
        if (isDebugMode) updateDebugInfo();
        return null;
      }

      const result = await response.json();

      // Check for GraphQL errors
      if (result.errors && result.errors.length > 0) {
        const errorMsg = result.errors.map(e => e.message).join(", ");
        console.error("Storefront API GraphQL errors:", result.errors);
        debugState.apiFetchSuccess = false;
        debugState.apiFetchError = "GraphQL: " + errorMsg;
        if (isDebugMode) updateDebugInfo();
        return null;
      }

      if (result.data && result.data.metaobject) {
        const presentedByField = result.data.metaobject.presented_by;
        if (!presentedByField || !presentedByField.value) {
          debugState.apiFetchSuccess = false;
          debugState.apiFetchError = "Portal found but presented_by field is empty";
          if (isDebugMode) updateDebugInfo();
          return null;
        }

        const metaobject = result.data.metaobject;
        const portalData = {
          presented_by: presentedByField.value,
          email_address: (metaobject.email_address && metaobject.email_address.value) || "",
          portal_id: (metaobject.portal_id && metaobject.portal_id.value) || "",
          show_address_and_phone: (metaobject.show_address_and_phone && metaobject.show_address_and_phone.value) || "",
          phone_number: (metaobject.phone_number && metaobject.phone_number.value) || "",
          formatted_address: (metaobject.formatted_address && metaobject.formatted_address.value) || "",
          display_profile_picture: (metaobject.display_profile_picture && metaobject.display_profile_picture.value) || "",
          profile_image: (metaobject.profile_image && metaobject.profile_image.value) || "",
          google_analytics_id: (metaobject.google_analytics_id && metaobject.google_analytics_id.value) || "",
          autoship_discount: (metaobject.autoship_discount && metaobject.autoship_discount.value) || "",
          vift_connect_active: (metaobject.vift_connect_active && metaobject.vift_connect_active.value) || "",
        };

        // Cache the successful response
        setCachedPortalData(portalId, portalData);

        // Keep the top-domain GA4 cookie in sync with the resolved portal
        syncGa4Cookie(portalData);

        debugState.apiFetchSuccess = true;
        debugState.portalData = portalData.presented_by;
        if (isDebugMode) updateDebugInfo();
        return portalData.presented_by;
      }

      debugState.apiFetchSuccess = false;
      debugState.apiFetchError = "Portal not found";
      if (isDebugMode) updateDebugInfo();
      return null;
    } catch (error) {
      console.error("Storefront API error:", error);
      debugState.apiFetchSuccess = false;
      debugState.apiFetchError = error instanceof Error ? error.message : "API error";
      if (isDebugMode) updateDebugInfo();
      return null;
    }
  }

  // ============================================================================
  // PORTAL RENDERING (Shop Consultant display in the announcement bar)
  // ============================================================================

  /**
   * Reveal the consultant line(s) and, when a name is supplied, set the consultant name.
   *
   * For anonymous users the consultant content is rendered hidden by Liquid
   * (header-announcements.liquid) and revealed here once the correct text is known,
   * so the fallback name never flashes. The consultant appears in multiple spots
   * (desktop zone, mobile carousel slide + its Swiper loop clones), so every matching
   * hook is updated.
   *
   * @param {string|null} presentedBy - The consultant name to display, or null/empty to
   *   reveal the server-rendered fallback without changing its text.
   * @param {string} source - Description of where the data came from (for debugging)
   * @returns {void}
   */
  function renderPortal(presentedBy, source) {
    const consultantNameEls = document.querySelectorAll("[data-shop-consultant-name]");
    const consultantWrapperEls = document.querySelectorAll("[data-shop-consultant]");

    if (consultantNameEls.length === 0 && consultantWrapperEls.length === 0) {
      if (isDebugMode) console.warn("Shop Consultant hooks not found");
      return;
    }

    // Only overwrite the text when we have a resolved name; otherwise keep the
    // server-rendered fallback that's already in the DOM.
    if (presentedBy) {
      consultantNameEls.forEach(el => {
        el.textContent = presentedBy;
      });
    }

    // Reveal (covers both the resolved-name and fallback reveal paths).
    consultantWrapperEls.forEach(el => {
      el.style.visibility = "";
      el.style.display = "";
    });

    if (isDebugMode) console.log("Shop Consultant revealed via JavaScript (" + source + "):", presentedBy);
    if (isDebugMode) updateDebugInfo();
  }

  /**
   * Reveal the server-rendered fallback consultant name for anonymous users when no
   * portal resolves (no cookie, cleared/invalid portal, or API failure). Without this,
   * the hidden-by-default markup would stay permanently hidden.
   * @returns {void}
   */
  function revealFallbackConsultant() {
    renderPortal(null, "fallback (no portal)");
  }

  /**
   * Populate and reveal the Contact page's consultant summary + direct-email form from a
   * resolved portal (MA-1852). On a UFO's replicated site the portal lives in the
   * portal_id cookie, which the section's Liquid can't read, so it renders the consultant
   * markup hidden (data-consultant-block) and this fills the name, the form recipient
   * (email_address) + portal id, and the phone/address the UFO chose to show, then reveals
   * it — the same cookie-resolved portal the header uses. No-op when the page has no
   * consultant hooks (not the contact page) or when no portal resolved (the corporate card
   * stays the only contact). It never runs on the fallback path. It also runs for a
   * logged-in customer's portal, so the sponsor section never has to look the portal up
   * itself; the sections drop the data-consultant-block hook for a customer who is not a
   * PPC, which makes this a no-op for them.
   *
   * @param {PortalData|null} portalData
   * @returns {void}
   */
  function renderContactConsultant(portalData) {
    if (!portalData || !portalData.presented_by) return;

    const blocks = document.querySelectorAll("[data-consultant-block]");
    if (blocks.length === 0) return; // not the contact page

    const name = portalData.presented_by;

    // Name — summary line and bio card.
    document.querySelectorAll("[data-consultant-name]").forEach(el => {
      el.textContent = name;
    });

    // "Email {name}" heading — re-interpolate from the localized template so i18n holds.
    document.querySelectorAll("[data-consultant-heading]").forEach(el => {
      const template = el.getAttribute("data-name-template");
      if (template) el.textContent = template.replace("__NAME__", name);
    });

    // Direct-email form recipient + portal id.
    document.querySelectorAll("[data-consultant-to-email]").forEach(el => {
      if (portalData.email_address) el.value = portalData.email_address;
    });
    document.querySelectorAll("[data-consultant-portal-id]").forEach(el => {
      el.value = portalData.portal_id || "";
    });

    // Phone + address, only when the UFO chose to display them.
    const showContact = portalData.show_address_and_phone === "true" || portalData.show_address_and_phone === true;
    if (portalData.phone_number) {
      document.querySelectorAll("[data-consultant-phone]").forEach(el => {
        el.textContent = portalData.phone_number;
      });
    }
    if (portalData.formatted_address) {
      document.querySelectorAll("[data-consultant-address]").forEach(el => {
        el.textContent = portalData.formatted_address;
      });
    }
    document.querySelectorAll("[data-consultant-contact]").forEach(el => {
      el.style.display = showContact ? "" : "none";
    });

    // Avatar, only when the UFO chose to display it. Portal Sync stores a relative path
    // (e.g. bin125/JASONP.jpg); the section owns the image server base and exposes it as
    // data-profile-image-base, so this only tidies the path and joins it on. An absolute
    // value is used as-is. Left hidden (as Liquid rendered it) when off or unresolvable.
    const showImage = portalData.display_profile_picture === "true" || portalData.display_profile_picture === true;
    const imageBase = document.querySelector("[data-profile-image-base]")?.getAttribute("data-profile-image-base") || "";
    const imagePath = (portalData.profile_image || "").trim();
    const imageSrc = /^(https?:)?\/\//i.test(imagePath)
      ? imagePath
      : imageBase && imagePath
        ? imageBase.replace(/\/+$/, "") + "/" + imagePath.replace(/^\/+/, "")
        : "";
    if (showImage && imageSrc) {
      document.querySelectorAll("[data-consultant-image]").forEach(img => {
        img.setAttribute("src", imageSrc);
        img.setAttribute("alt", name);
      });
      document.querySelectorAll("[data-consultant-image-wrap]").forEach(el => {
        el.style.display = "";
      });
    }

    // Reveal the blocks Liquid rendered hidden for the client-resolved path.
    blocks.forEach(el => {
      el.style.display = "";
      el.style.visibility = "";
    });
  }

  // ============================================================================
  // CART TRACKING
  // ============================================================================

  /**
   * Get the current portal ID from customer metafield or cookie
   * @returns {string|null} The portal ID, or null if not available
   */
  function getPortalId() {
    // Priority: portalWindow.customerPortalId (from Liquid) > cookie
    if (portalWindow.customerPortalId) {
      return portalWindow.customerPortalId;
    }

    // Fallback: try to read from cookie
    return getCookie("portal_id");
  }

  /**
   * Update the cart with portal session and credit user attributes
   * @param {string} portalId - The portal ID to set as cart attribute
   * @param {string|null} [creditUser] - Optional credit_user to set as cart attribute
   * @returns {Promise<Object|void>} Resolves with the cart object on success, or void if no portalId
   */
  function updateCartWithPortalSession(portalId, creditUser) {
    if (!portalId) {
      if (isDebugMode) console.warn("No portal ID provided for cart update");
      return Promise.resolve();
    }

    // Build attributes object dynamically
    // Always include credit_user (even as empty string) to clear stale cart attributes
    /** @type {{portal_session: string, credit_user: string}} */
    const attributes = {
      portal_session: portalId,
      credit_user: creditUser || "",
    };

    return fetch("/cart/update.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ attributes }),
    })
      .then(response => {
        if (!response.ok) {
          throw new Error(`Cart update failed: ${response.status}`);
        }
        return response.json();
      })
      .then(cart => {
        if (isDebugMode) {
          console.log("Cart attributes updated (Ajax API):", { portal_session: portalId, credit_user: creditUser });
        }
        return cart;
      })
      .catch(error => {
        console.error("Error updating cart attributes:", error);
        throw error;
      });
  }

  /**
   * Initialize cart tracking with the current portal ID and credit user
   * @returns {Promise<void>}
   */
  function initCartTracking() {
    const portalId = getPortalId();
    const creditUserRaw = getCreditUser();

    // Validate credit_user from cookie - clear if invalid
    let creditUser = null;
    if (creditUserRaw) {
      if (isValidCreditUser(creditUserRaw)) {
        creditUser = creditUserRaw;
      } else {
        // Invalid credit_user in cookie - clear it
        console.warn("Invalid credit_user format in cookie, clearing:", creditUserRaw);
        clearCookie("credit_user");
      }
    }

    // Skip the API call if cart attributes from the latest Liquid render already match.
    const { portal_session = "", credit_user = "" } = globalConfig.cartAttributes || {};
    if (portal_session === (portalId || "") && credit_user === (creditUser || "")) {
      if (isDebugMode) {
        console.log("Cart attributes already match, skipping update:", { portal_session, credit_user });
      }
      return Promise.resolve();
    }

    if (portalId) {
      return updateCartWithPortalSession(portalId, creditUser)
        .then(() => {
          if (isDebugMode) {
            console.log("Cart tracking initialized successfully:", { portal: portalId, creditUser: creditUser });
          }
        })
        .catch(error => {
          console.error("Failed to initialize cart tracking:", error);
        });
    } else {
      if (isDebugMode) console.warn("No portal ID available for cart tracking");
      return Promise.resolve();
    }
  }

  // ============================================================================
  // URL PARAMETER HANDLING
  // ============================================================================

  /**
   * Handle portal_id and credit_user URL parameters for setting or clearing portal context
   * @returns {Promise<void>}
   */
  function handleUrlParameter() {
    if (hasCustomerPortal) {
      if (isDebugMode) console.log("Customer has portal_id, ignoring URL/cookie");
      return Promise.resolve();
    }

    const urlParams = new URLSearchParams(window.location.search);
    const urlPortalId = urlParams.get("portal_id");
    const urlCreditUser = urlParams.get("credit_user");
    const existingPortalId = getCookie("portal_id");

    // Track if portal_id is changing (credit_user should be cleared when portal changes)
    // Only consider it a change if the new portal_id is valid
    const portalIsChanging =
      urlPortalId !== null && urlPortalId !== "" && isValidPortalId(urlPortalId) && urlPortalId !== existingPortalId;

    // Handle credit_user URL parameter
    if (urlCreditUser !== null) {
      if (urlCreditUser === "") {
        // Empty value clears credit_user cookie
        clearCookie("credit_user");
        debugState.creditUser = null;
        if (isDebugMode) console.log("Credit user cookie cleared via URL parameter");
      } else if (isValidCreditUser(urlCreditUser)) {
        // Valid credit_user - set cookie
        setCookie("credit_user", urlCreditUser, CONFIG.cookieExpiration);
        debugState.creditUser = urlCreditUser;
        if (isDebugMode) console.log("Credit user cookie set from URL:", urlCreditUser);
      } else {
        // Invalid format - warn but don't set
        console.warn(
          "Invalid credit_user format:",
          urlCreditUser,
          "(expected: C[customer] or R[UFO repID] followed by digits, max 16 chars)"
        );
        // If the portal is changing, clear any existing credit_user to avoid stale attribution
        if (portalIsChanging) {
          clearCookie("credit_user");
          debugState.creditUser = null;
          if (isDebugMode) console.log("Credit user cookie cleared (invalid URL credit_user on portal change)");
        }
      }
    } else if (portalIsChanging) {
      // Portal is changing - clear credit_user since it's tied to the previous portal
      clearCookie("credit_user");
      debugState.creditUser = null;
      if (isDebugMode) console.log("Credit user cookie cleared (portal_id changed)");
    } else {
      // Load credit_user from cookie for debug state
      debugState.creditUser = getCreditUser();
    }

    if (urlPortalId !== null) {
      if (urlPortalId === "") {
        // Clear cookie, cache, and remove from URL
        clearCookie("portal_id");
        clearCookie("credit_user"); // Also clear credit_user when portal is cleared
        clearCookie(GA4_COOKIE); // GA4 cookie is tied to the portal session
        clearPortalCache(); // Clear all portal cache
        debugState.creditUser = null;

        if (isDebugMode) console.log("Portal and credit_user cookies/cart cleared via URL parameter");
        const searchParams = new URLSearchParams(window.location.search);
        searchParams.delete("portal_id");
        searchParams.delete("credit_user");
        const searchString = searchParams.toString();
        const newUrl = window.location.pathname + (searchString ? "?" + searchString : "");
        window.history.replaceState({}, "", newUrl);

        // No portal anymore - reveal the fallback consultant name
        revealFallbackConsultant();

        // Clear cart attributes to remove stale attribution
        return fetch("/cart/update.js", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: { portal_session: "", credit_user: "" } }),
        }).then(() => {}, error => {
          console.error("Error clearing cart attributes:", error);
        });
      } else if (isValidPortalId(urlPortalId)) {
        // Valid portal_id - set cookie and fetch portal data
        setCookie("portal_id", urlPortalId, CONFIG.cookieExpiration);
        if (isDebugMode) console.log("Portal cookie set from URL:", urlPortalId);

        return fetchPortalData(urlPortalId, "URL parameter").then(presentedBy => {
          if (presentedBy) {
            renderPortal(presentedBy, "URL parameter");
            renderContactConsultant(getCachedPortalData(urlPortalId));
          } else {
            // Fetch failed or portal not found - reveal the fallback so the line isn't stuck hidden
            revealFallbackConsultant();
            if (!CONFIG.storefrontToken) {
              console.error("Unable to load portal:", urlPortalId, "- missing Storefront token.");
            } else {
              console.error("Failed to load portal (not found or API error):", urlPortalId);
            }
          }
        });
      } else {
        // Invalid portal_id format - warn but don't set
        console.warn(
          "Invalid portal_id format:",
          urlPortalId,
          "(expected: alphanumeric with hyphens/underscores, max 64 chars)"
        );
        revealFallbackConsultant();
        return Promise.resolve();
      }
    } else {
      // Check cookie
      const cookiePortalId = getCookie("portal_id");
      if (cookiePortalId) {
        if (isValidPortalId(cookiePortalId)) {
          if (isDebugMode) console.log("Loading portal from cookie:", cookiePortalId);
          return fetchPortalData(cookiePortalId, "cookie").then(presentedBy => {
            if (presentedBy) {
              renderPortal(presentedBy, "cookie");
              renderContactConsultant(getCachedPortalData(cookiePortalId));
            } else {
              // Fetch failed or portal not found - reveal the fallback
              revealFallbackConsultant();
            }
          });
        } else {
          // Invalid portal_id in cookie - clear it and reveal the fallback
          console.warn("Invalid portal_id format in cookie, clearing:", cookiePortalId);
          clearCookie("portal_id");
          clearCookie(GA4_COOKIE);
          revealFallbackConsultant();
          return Promise.resolve();
        }
      } else {
        // Anonymous user with no portal cookie - reveal the server-rendered fallback
        revealFallbackConsultant();
        return Promise.resolve();
      }
    }
  }

  // ============================================================================
  // DEBUG MODE UI
  // ============================================================================

  /**
   * Escape HTML so cookie/URL values can't inject markup when set via innerHTML.
   * @param {string} value
   * @returns {string}
   */
  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /**
   * Reveal the Liquid-rendered debug container and populate its dynamic slot.
   * @returns {void}
   */
  function showDebugUI() {
    if (!isDebugMode) return;
    /** @type {HTMLElement | null} */
    const debugContainer = document.querySelector("[data-portal-debug]");
    if (!debugContainer) return;
    debugContainer.style.display = "block";
    updateDebugInfo();
  }

  /**
   * Update the debug UI panel with current client-side state.
   * Only touches the [data-js-debug] slot; the static Liquid markup is left alone.
   * @returns {void}
   */
  function updateDebugInfo() {
    if (!isDebugMode) return;

    const debugDiv = document.querySelector("[data-js-debug]");
    if (!debugDiv) return;

    const urlParams = new URLSearchParams(window.location.search);
    const cookiePortalId = getCookie("portal_id");
    const cookieCreditUser = getCreditUser();
    const cookieGa4 = getCookie(GA4_COOKIE);

    // Read cached fields for the active portal directly (no debugState side effects).
    let cachedGa4 = "";
    let cachedDiscount = "";
    const activePortalId = currentPortalId || cookiePortalId;
    if (activePortalId && isLocalStorageAvailable()) {
      try {
        const raw = localStorage.getItem(`portal_cache_${activePortalId}`);
        const entry = raw ? JSON.parse(raw) : null;
        if (entry && entry.data) {
          cachedGa4 = entry.data.google_analytics_id || "";
          cachedDiscount = entry.data.autoship_discount || "";
        }
      } catch {
        /* ignore malformed cache in debug view */
      }
    }
    /** @type {HTMLElement | null} */
    const shopConsultant = document.querySelector("[data-shop-consultant]");
    const consultantIsVisible = shopConsultant
      ? shopConsultant.style.visibility !== "hidden" && shopConsultant.style.display !== "none"
      : false;

    let cacheStatusHtml;
    if (debugState.cacheHit) {
      cacheStatusHtml = '<span style="color:#2e7d32;">Hit</span>';
    } else if (debugState.cacheMiss) {
      cacheStatusHtml = '<span style="color:#f57c00;">Miss</span>';
    } else {
      cacheStatusHtml = "Not checked";
    }

    let apiStatusHtml = "";
    if (debugState.apiFetchAttempted) {
      apiStatusHtml += `<div><strong>API Fetch Attempted: </strong>Yes (${escapeHtml(debugState.portalSource || "")})</div>`;
      apiStatusHtml += `<div><strong>API Fetch Success: </strong>${debugState.apiFetchSuccess ? "Yes" : "No"}</div>`;
      if (debugState.apiFetchSuccess && debugState.portalData) {
        apiStatusHtml += `<div><strong>Portal Data Retrieved: </strong>${escapeHtml(debugState.portalData)}</div>`;
      }
      if (debugState.apiFetchError) {
        apiStatusHtml += `<div style="color:#d32f2f;"><strong>API Error: </strong>${escapeHtml(debugState.apiFetchError)}</div>`;
      }
    } else if (debugState.cacheHit) {
      apiStatusHtml += `<div><strong>API Fetch Attempted: </strong>No (cache hit)</div>`;
      if (debugState.portalData) {
        apiStatusHtml += `<div><strong>Portal Data Retrieved: </strong>${escapeHtml(debugState.portalData)} (from cache)</div>`;
      }
    } else {
      apiStatusHtml += `<div><strong>API Fetch Attempted: </strong>No</div>`;
    }

    debugDiv.innerHTML = `
      <div style="background:#f3e5f5;padding:0.5rem;margin:0.5rem 0;border-radius:4px;">
        <strong>JavaScript (Client-Side):</strong><br>
        <div><strong>URL portal_id: </strong>${escapeHtml(urlParams.get("portal_id") || "None")}</div>
        <div><strong>Cookie portal_id: </strong>${escapeHtml(cookiePortalId || "None")}</div>
        <div><strong>URL credit_user: </strong>${escapeHtml(urlParams.get("credit_user") || "None")}</div>
        <div><strong>Cookie credit_user: </strong>${escapeHtml(cookieCreditUser || "None")}</div>
        <div><strong>Cache Status: </strong>${cacheStatusHtml}</div>
        ${apiStatusHtml}
        <div><strong>Cookie portal_ga4: </strong>${escapeHtml(cookieGa4 || "None")}</div>
        <div><strong>Cached GA4 ID: </strong>${escapeHtml(cachedGa4 || "None")}</div>
        <div><strong>Cached Autoship Discount: </strong>${escapeHtml(cachedDiscount || "None")}</div>
        <div><strong>Shop Consultant Visible: </strong>${consultantIsVisible ? "Yes" : "No"}</div>
      </div>
    `;
  }

  // ============================================================================
  // DO-NOT-SELL CONSENT
  // ============================================================================

  /**
   * If the customer's privacy.do_not_sell metafield is true, set sale_of_data: false
   * via the Shopify consent-tracking-api. The metafield value is rendered into
   * window.customerDoNotSell by portal-manager.liquid at page load.
   * @returns {void}
   */
  function applyDoNotSellConsent() {
    if (portalWindow.customerDoNotSell !== true) return;
    if (!window.Shopify || typeof window.Shopify.loadFeatures !== "function") return;

    window.Shopify.loadFeatures([
      {
        name: "consent-tracking-api",
        version: "0.1",
        onLoad: function (errors) {
          if (errors) { console.error("Failed to load consent-tracking-api:", errors); return; }
          const shopify = /** @type {any} */ (window.Shopify);
          if (!shopify.customerPrivacy) { console.error("customerPrivacy API unavailable after load"); return; }
          shopify.customerPrivacy.setTrackingConsent(
            { sale_of_data: false, analytics: false, marketing: false },
            () => { if (isDebugMode) console.log("Do-not-sell preference applied."); }
          );
        },
      },
    ]);
  }

  // ============================================================================
  // INITIALIZATION
  // ============================================================================

  /**
   * Initialize the Portal Manager module
   * @returns {Promise<void>}
   */
  async function init() {
    // For logged-in users, the consultant name is rendered server-side via Liquid
    // (header-announcements.liquid) and is never hidden, so no JS reveal is needed.

    // Mirror the APIM auth token meta tag into a cookie (or clear it on logout)
    syncApimAuthTokenCookie();

    // Apply do-not-sell consent preference if the customer metafield is set
    applyDoNotSellConsent();

    // Handle URL parameter and cookie for anonymous users, and update cart tracking.
    // Both are awaited so portal-manager:ready fires only after all async work settles.
    await Promise.all([handleUrlParameter(), initCartTracking()]);

    if (isDebugMode) {
      if (document.readyState === "loading") {
        document.addEventListener("DOMContentLoaded", showDebugUI);
      } else {
        showDebugUI();
      }
    }

    document.dispatchEvent(new CustomEvent("portal-manager:ready"));
  }

  // Public, read-only accessor so add-to-cart code (product-form.js) can stamp the
  // active portal_id onto line items without duplicating cookie/metafield resolution.
  // Set synchronously at script execution (function declarations are hoisted), so it's
  // available before any user add-to-cart click. Returns null when no valid portal is set.
  portalWindow.PortalManager = {
    getPortalId: function () {
      const id = getPortalId();
      return id && isValidPortalId(id) ? id : null;
    },

    /**
     * Resolve the full PortalData (presented_by, email_address, etc.) for the
     * active portal (customer metafield or portal_id cookie), for callers that
     * need more than just the id (e.g. catalog-share.js emailing a consultant).
     * Serves from the localStorage cache when available; otherwise fetches from
     * the Storefront API and caches the result, same as the internal resolution
     * paths above.
     * @returns {Promise<PortalData|null>}
     */
    getPortalData: async function () {
      const id = getPortalId();
      if (!id || !isValidPortalId(id)) return null;

      const cached = getCachedPortalData(id);
      if (cached) return cached;

      await fetchPortalData(id, "public API");
      return getCachedPortalData(id);
    },

    /**
     * Resolve the sponsor's credit_user value for carrying attribution through to
     * the Join site (e.g. popup-modal.js's Start Now link/redirect). Checks the
     * credit_user URL parameter first, then falls back to the credit_user cookie.
     * Only ever reads anonymous attribution state - never customer/signed-in data.
     *
     * Only a `C` value is returned. `C` carries a pcID and `R` a UnFranchise repID -
     * two different id spaces - and the Join site strips the prefix and looks the
     * remainder up as a pcID. An `R` value would therefore miss, or worse resolve to
     * a different person, so it is treated as no sponsor at all. Share links built by
     * the theme are always `C` (snippets/meta-tags.liquid); an `R` can only arrive on
     * a link minted elsewhere.
     * @returns {string|null} A validated `C` credit_user value, or null if none is set.
     */
    getCreditUser: function () {
      const isSponsorId = (value) => isValidCreditUser(value) && value.startsWith("C");

      const urlCreditUser = new URLSearchParams(window.location.search).get("credit_user");
      if (urlCreditUser && isSponsorId(urlCreditUser)) return urlCreditUser;

      const cookieCreditUser = getCreditUser();
      if (cookieCreditUser && isSponsorId(cookieCreditUser)) return cookieCreditUser;

      return null;
    },
  };

  // Start initialization
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
